import { z } from 'zod';

// Enum schemas
export const employmentTypeSchema = z.enum([
  'FULL_TIME',
  'PART_TIME',
  'CONTRACT',
  'FREELANCE',
  'INTERNSHIP',
]);

export const remotePolicySchema = z.enum(['REMOTE', 'HYBRID', 'ON_SITE']);

export const experienceLevelSchema = z.enum([
  'ENTRY',
  'JUNIOR',
  'MID',
  'SENIOR',
  'LEAD',
  'EXECUTIVE',
]);

export const educationLevelSchema = z.enum([
  'HIGH_SCHOOL',
  'ASSOCIATE',
  'BACHELOR',
  'MASTER',
  'PHD',
]);

// Helper to handle empty string → null for optional numeric fields
const optionalNumber = z.preprocess(
  (val) => (val === '' || val === undefined ? null : Number(val)),
  z.number().min(0).optional().nullable()
);

const optionalIntNumber = z.preprocess(
  (val) => (val === '' || val === undefined ? null : Number(val)),
  z.number().int().min(0).optional().nullable()
);

// Create job position schema
export const createJobPositionSchema = z
  .object({
    title: z.string().min(1, 'Title is required').max(255, 'Title must be at most 255 characters'),
    description: z.string().min(1, 'Description is required'),
    department: z
      .string()
      .max(100, 'Department must be at most 100 characters')
      .optional()
      .nullable(),
    location: z
      .string()
      .min(1, 'Location is required')
      .max(255, 'Location must be at most 255 characters'),
    requirements: z.string().optional().nullable(),
    responsibilities: z.string().optional().nullable(),
    benefits: z.string().optional().nullable(),
    employmentType: employmentTypeSchema,
    remotePolicy: remotePolicySchema,
    experienceLevels: z
      .array(experienceLevelSchema)
      .min(1, 'At least one experience level is required'),
    yearsOfExperience: optionalIntNumber,
    salaryMin: optionalNumber,
    salaryMax: optionalNumber,
    salaryCurrency: z.string().max(3, 'Currency code must be 3 characters').default('USD'),
    skills: z.array(z.string()).default([]),
    requiredSkills: z.array(z.string()).default([]),
    preferredSkills: z.array(z.string()).default([]),
    technologies: z.array(z.string()).default([]),
    educationLevel: educationLevelSchema.optional().nullable(),
    educationField: z
      .string()
      .max(100, 'Education field must be at most 100 characters')
      .optional()
      .nullable(),
    languages: z.array(z.string()).default([]),
    certifications: z.array(z.string()).default([]),
    jobPostingDate: z.string().min(1, 'Job posting date is required'),
    applicationDeadline: z.string().min(1, 'Application deadline is required'),
    startDate: z.string().optional().nullable(),
    applicationUrl: z
      .string()
      .max(500, 'Application URL must be at most 500 characters')
      .optional()
      .nullable(),
  })
  .refine(
    (data) => {
      if (data.salaryMin != null && data.salaryMax != null) {
        return data.salaryMin <= data.salaryMax;
      }
      return true;
    },
    {
      message: 'Minimum salary must be less than or equal to maximum salary',
      path: ['salaryMax'],
    }
  )
  .refine(
    (data) => {
      const postingDate = new Date(data.jobPostingDate);
      const deadlineDate = new Date(data.applicationDeadline);
      return postingDate <= deadlineDate;
    },
    {
      message: 'Application deadline must be after job posting date',
      path: ['applicationDeadline'],
    }
  );

export type CreateJobPositionFormData = z.infer<typeof createJobPositionSchema>;

// Update job position schema (same as create)
export const updateJobPositionSchema = createJobPositionSchema;

export type UpdateJobPositionFormData = z.infer<typeof updateJobPositionSchema>;
